// Implicit Primitives — curated LEAP71 ShapeKernel patterns surfaced as
// first-class features. Each primitive has a hardcoded C# template living
// in implicit-microservice/Program.cs; the frontend only sends parameters,
// never code. We never trust an LLM to write ShapeKernel.
//
// Override at build time with VITE_IMPLICIT_URL — useful for hitting a
// local Fly proxy or a `dotnet run` instance during development.

export const IMPLICIT_PRIMITIVES_BASE_URL: string =
  import.meta.env.VITE_IMPLICIT_URL ?? 'https://telekinesis-implicit.fly.dev';

export type ImplicitPrimitive =
  // Base primitives (BaseBox, BaseSphere, etc. from LEAP71 ShapeKernel)
  | 'base-box'
  | 'base-sphere'
  | 'base-cylinder'
  | 'base-pipe'
  | 'base-pipe-segment'
  | 'base-ring'
  | 'base-lens'
  // Implicit SDF shapes
  | 'super-ellipsoid'
  | 'gyroid-sphere'
  | 'gyroid-genus'
  // Lattices
  | 'lattice-pipe'
  | 'lattice-manifold'
  // NASA Unitcell Lattice Converter — fills an uploaded STL/STEP part with a
  // chosen NASA lattice style on the PicoGK voxel kernel. Distinct from the
  // single-cell nasa-lattice-* topologies: one converter, style carried in
  // params (see LatticeFillParams).
  | 'lattice-fill'
  // Composite tutorial primitives
  | 'rover-wheel'
  | 'helix-heat-x'
  // NASA Unitcell Engine topologies (unitcellhub/unitcellengine). Each maps
  // to a canonical tutorial cell — 11 graph-style from
  // src/unitcellengine/geometry/definitions/*.ltcx, 3 walled-TPMS from
  // sdf.py::_SDFS_WALLED_TPMS. All gated as `comingSoon` in the toolbar
  // dropdown until the Python sidecar microservice ships — see
  // docs/issue-nasa-unitcell-engine-integration-hell.md.
  | NasaLatticeTopology;

// Split out so we can iterate the 14 topologies without pattern-matching the
// parent union. The 'nasa-lattice-' prefix is load-bearing — Toolbar's pick
// handler tests `startsWith('nasa-lattice-')` to route every new topology
// through the same param-defaults branch.
export type NasaLatticeTopology =
  // Graph-style topologies (.ltcx files)
  | 'nasa-lattice-octet'
  | 'nasa-lattice-simple-cubic'
  | 'nasa-lattice-bcc'
  | 'nasa-lattice-fcc'
  | 'nasa-lattice-kelvin'
  | 'nasa-lattice-diamond'
  | 'nasa-lattice-isotruss'
  | 'nasa-lattice-truncated-octahedron'
  | 'nasa-lattice-weaire-phelan'
  | 'nasa-lattice-hexagonal-honeycomb'
  | 'nasa-lattice-reentrant'
  // Walled TPMS — the three entries the engine actually ships in
  // sdf.py::_SDFS_WALLED_TPMS. (Lidinoid was in an earlier draft but isn't
  // wired in upstream; swapped for Diamond TPMS, which IS supported and
  // is structurally distinct from the graph-style Diamond above. Same name
  // on the engine side, disambiguated by form='walledtpms'.)
  | 'nasa-lattice-gyroid'
  | 'nasa-lattice-schwarz'
  | 'nasa-lattice-diamond-tpms';

export const IMPLICIT_PRIMITIVE_LABELS: Record<ImplicitPrimitive, string> = {
  // Display labels drop the "Base" prefix that LEAP71 uses internally —
  // "Box" reads cleaner in a UI than "Base Box". The slug keeps `base-*` to
  // match the LEAP71 source files for code-maintainability traceability.
  'base-box': 'Box',
  'base-sphere': 'Sphere',
  'base-cylinder': 'Cylinder',
  'base-pipe': 'Pipe',
  'base-pipe-segment': 'Pipe Segment',
  'base-ring': 'Ring',
  'base-lens': 'Lens',
  'super-ellipsoid': 'Super Ellipsoid',
  'gyroid-sphere': 'Gyroid Sphere',
  'gyroid-genus': 'Gyroid Genus',
  'lattice-pipe': 'Lattice Pipe',
  'lattice-manifold': 'Lattice Manifold',
  'lattice-fill': 'NASA Unitcell Lattice Converter',
  'rover-wheel': 'Lunar Rover Wheel',
  'helix-heat-x': 'Helical Bimetal Liquid-Liquid',
  // NASA Unitcell Engine labels mirror the unitcellengine canonical names so
  // a user searching the unitcellhub docs lands on the same noun. "BCC" /
  // "FCC" expanded since "Body Centered Cubic" reads cleaner in a dropdown
  // than the acronym alone (acronym shown parenthetically).
  'nasa-lattice-octet': 'Octet',
  'nasa-lattice-simple-cubic': 'Simple Cubic',
  'nasa-lattice-bcc': 'Body Centered Cubic (BCC)',
  'nasa-lattice-fcc': 'Face Centered Cubic (FCC)',
  'nasa-lattice-kelvin': 'Kelvin Cell',
  'nasa-lattice-diamond': 'Diamond',
  'nasa-lattice-isotruss': 'IsoTruss',
  'nasa-lattice-truncated-octahedron': 'Truncated Octahedron',
  'nasa-lattice-weaire-phelan': 'Weaire-Phelan',
  'nasa-lattice-hexagonal-honeycomb': 'Hexagonal Honeycomb',
  'nasa-lattice-reentrant': 'Re-entrant (Auxetic)',
  'nasa-lattice-gyroid': 'Gyroid (TPMS)',
  'nasa-lattice-schwarz': 'Schwarz (TPMS)',
  'nasa-lattice-diamond-tpms': 'Diamond (TPMS)',
};

// Display order for the NASA Lattice Primitives dropdown. Active topologies
// first, disabled (see NASA_LATTICE_DISABLED below) sorted to the bottom of
// the dropdown so the visually-distinct grey rows don't break up the active
// set. Within each group: graph topologies before walled-TPMS, matching the
// unitcellengine internal partition (graph .ltcx files vs sdf.py walled-TPMS
// map). Octet leads because it's the engine's tutorial reference cell.
//
// `nasa-lattice-simple-cubic` is intentionally commented out here — it's a
// literal 3D strut grid and not visually interesting. Slug, label, and
// ENGINE_MAP entry are preserved so re-enabling is a one-line uncomment.
export const NASA_LATTICE_TOPOLOGY_ORDER: NasaLatticeTopology[] = [
  // Heroed (large card with hero image + center-area hero swap). Kelvin Cell
  // leads. See NASA_LATTICE_HEROES below for the asset map.
  'nasa-lattice-kelvin',
  'nasa-lattice-octet',
  'nasa-lattice-isotruss',
  'nasa-lattice-reentrant',
  // Active but no hero asset yet — small text-only dropdown items.
  // 'nasa-lattice-simple-cubic',  // hidden — not visually interesting
  'nasa-lattice-bcc',
  'nasa-lattice-fcc',
  'nasa-lattice-truncated-octahedron',
  'nasa-lattice-diamond-tpms',
  // Disabled (Coming soon. — see NASA_LATTICE_DISABLED + the failures issue
  // doc). Kept in the list so the dropdown still shows them, greyed out.
  // 'nasa-lattice-diamond',
  'nasa-lattice-hexagonal-honeycomb',
  'nasa-lattice-weaire-phelan',
  'nasa-lattice-gyroid',
  'nasa-lattice-schwarz',
];

// Hero image assets for the NASA Lattice Primitives dropdown. Partial because
// most topologies don't have hand-rendered hero assets yet. Topologies present
// in this map get the big-card treatment in TopToolbar (hero on the right,
// label on the left, matching the Wheels/SUPIN dropdown chrome) AND show
// their hero in the center viewing area when active. Topologies absent fall
// back to the existing small text-only dropdown rows.
export const NASA_LATTICE_HEROES: Partial<Record<NasaLatticeTopology, string>> =
  {
    'nasa-lattice-kelvin': '/nasa-lattice-kelvin-hero.png',
    'nasa-lattice-octet': '/nasa-lattice-octet-hero.png',
    'nasa-lattice-isotruss': '/nasa-lattice-isotruss-hero.png',
    'nasa-lattice-reentrant': '/nasa-lattice-reentrant-hero.png',
  };

// Topologies that render in the dropdown but are not selectable. Each maps
// to a real engine failure mode caught during Phase 5C–F UI testing — either
// edge function 504 IDLE_TIMEOUT or visibly incomplete geometry. See
// docs/issue-unitcell-primitives-failures.md for the per-topology root cause.
export const NASA_LATTICE_DISABLED: ReadonlySet<NasaLatticeTopology> = new Set([
  'nasa-lattice-diamond', // incomplete geometry
  'nasa-lattice-gyroid', // 504 IDLE_TIMEOUT (TPMS)
  'nasa-lattice-schwarz', // 504 IDLE_TIMEOUT (TPMS, anticipated)
  'nasa-lattice-hexagonal-honeycomb', // 504 IDLE_TIMEOUT
  'nasa-lattice-weaire-phelan', // anticipated 504 (highest complexity)
]);

// Maps frontend slugs to the unitcellengine canonical names. The engine
// accepts these as the first positional arg to UnitcellDesign(). Mismatches
// here = silent KeyError in the Python sidecar, so this is the load-bearing
// translation layer. Sources:
//   - graph names: src/unitcellengine/geometry/definitions/*.ltcx filenames
//   - TPMS names:  src/unitcellengine/geometry/sdf.py::_SDFS_WALLED_TPMS keys
// Form distinguishes the two geometry pipelines — 'walledtpms' uses the
// SDF-based walled minimal-surface generator, 'graph' uses the strut-and-node
// lattice generator. The engine's UnitcellDesign accepts form= as a kwarg.
export const NASA_LATTICE_ENGINE_MAP: Record<
  NasaLatticeTopology,
  { name: string; form: 'graph' | 'walledtpms' }
> = {
  'nasa-lattice-octet': { name: 'Octet', form: 'graph' },
  'nasa-lattice-simple-cubic': { name: 'Simple cubic', form: 'graph' },
  'nasa-lattice-bcc': { name: 'Body centered cubic', form: 'graph' },
  'nasa-lattice-fcc': { name: 'Face centered cubic', form: 'graph' },
  'nasa-lattice-kelvin': { name: 'Kelvin cell', form: 'graph' },
  'nasa-lattice-diamond': { name: 'Diamond', form: 'graph' },
  'nasa-lattice-isotruss': { name: 'IsoTruss', form: 'graph' },
  'nasa-lattice-truncated-octahedron': {
    name: 'Truncated octahedron',
    form: 'graph',
  },
  'nasa-lattice-weaire-phelan': { name: 'Weaire-Phelan', form: 'graph' },
  'nasa-lattice-hexagonal-honeycomb': {
    name: 'Hexagonal honeycomb',
    form: 'graph',
  },
  'nasa-lattice-reentrant': { name: 'Re-entrant', form: 'graph' },
  'nasa-lattice-gyroid': { name: 'Gyroid', form: 'walledtpms' },
  'nasa-lattice-schwarz': { name: 'Schwarz', form: 'walledtpms' },
  'nasa-lattice-diamond-tpms': { name: 'Diamond', form: 'walledtpms' },
};

// Defaults mirror the LEAP71 examples directory verbatim — same numbers the
// reference C# files in implicit-microservice/ShapeKernel/Examples/ use. If
// you bump any of these, also bump the matching fallback in Program.cs so
// an empty-payload request still produces the canonical reference shape.
export interface GyroidSphereParams {
  radius: string;
  period: string;
  ratio: string;
}
export const GYROID_SPHERE_DEFAULTS: GyroidSphereParams = {
  radius: '10',
  period: '3',
  ratio: '1',
};

export interface SuperEllipsoidParams {
  ax: string;
  ay: string;
  az: string;
  epsilon1: string;
  epsilon2: string;
}
// Defaults scaled up 10× from the LEAP71 example so the shape sits at a
// reasonable viewer scale. The math is identical at any scale — exponents
// e1/e2 are the only shape parameters — so this change is purely about
// keeping the camera-fit auto-zoom from thrashing on a 1mm-wide object.
export const SUPER_ELLIPSOID_DEFAULTS: SuperEllipsoidParams = {
  ax: '10',
  ay: '10',
  az: '10',
  epsilon1: '3.0',
  epsilon2: '0.25',
};

export interface LatticePipeParams {
  length: string;
  radius: string;
}
export const LATTICE_PIPE_DEFAULTS: LatticePipeParams = {
  length: '60',
  radius: '10',
};

export interface GyroidGenusParams {
  gap: string;
  extent: string;
  period: string;
  ratio: string;
}
export const GYROID_GENUS_DEFAULTS: GyroidGenusParams = {
  gap: '0.3',
  extent: '2.5',
  period: '1',
  ratio: '0.5',
};

// Base primitives — defaults match the LEAP71 ``Ex_Base*ShowCase`` files'
// "basic" sub-blocks verbatim. Bumping any of these means also bumping the
// matching fallback in implicit-microservice/Program.cs so empty payloads
// still produce the canonical reference shape.

export interface BaseBoxParams {
  height: string;
  width: string;
  depth: string;
}
export const BASE_BOX_DEFAULTS: BaseBoxParams = {
  height: '20',
  width: '10',
  depth: '15',
};

export interface BaseSphereParams {
  radius: string;
}
export const BASE_SPHERE_DEFAULTS: BaseSphereParams = {
  radius: '40',
};

export interface BaseCylinderParams {
  length: string;
  radius: string;
}
export const BASE_CYLINDER_DEFAULTS: BaseCylinderParams = {
  length: '60',
  radius: '40',
};

export interface BasePipeParams {
  length: string;
  innerRadius: string;
  outerRadius: string;
}
export const BASE_PIPE_DEFAULTS: BasePipeParams = {
  length: '60',
  innerRadius: '10',
  outerRadius: '20',
};

export interface BasePipeSegmentParams {
  length: string;
  innerRadius: string;
  outerRadius: string;
  // phiMid + phiRange are radians. The LEAP71 example uses π and 0.5π
  // respectively; we surface those as strings so the sidebar can edit them.
  phiMid: string;
  phiRange: string;
}
export const BASE_PIPE_SEGMENT_DEFAULTS: BasePipeSegmentParams = {
  length: '60',
  innerRadius: '20',
  outerRadius: '40',
  phiMid: '3.14159',
  phiRange: '1.5708',
};

export interface BaseRingParams {
  ringRadius: string;
  tubeRadius: string;
}
export const BASE_RING_DEFAULTS: BaseRingParams = {
  ringRadius: '30',
  tubeRadius: '8',
};

export interface BaseLensParams {
  height1: string;
  height2: string;
  radius: string;
}
export const BASE_LENS_DEFAULTS: BaseLensParams = {
  height1: '10',
  height2: '10',
  radius: '40',
};

// Lattice Manifold has a bool (extendBothSides) alongside three floats —
// the sidebar renders it as a checkbox; the params type stores it as a
// stringified 'true' / 'false' so the same string-map machinery applies.
export interface LatticeManifoldParams {
  length: string;
  radius: string;
  overhangAngle: string;
  extendBothSides: string;
}
export const LATTICE_MANIFOLD_DEFAULTS: LatticeManifoldParams = {
  length: '50',
  radius: '5',
  overhangAngle: '45',
  extendBothSides: 'false',
};

// Rover Wheel — port of LEAP71_RoverWheel. Variant is one of "1"/"2"/"3"/"4"
// (preset wheels) / "0" (RandomWheel). Seed is only meaningful when
// variant === '0'.
//
// `outerRadius`, `hubRadius`, `width`, `treadPattern` are PREVIEW fields —
// rendered in the sidebar today but not yet wired to the C# RoverWheelRequest
// (which still only accepts variant + seed). They round-trip through the
// supabase edge function but are silently ignored server-side until #37
// (parametric wheel) lands.
//
// Defaults are intentionally empty so the sidebar dropdowns and parameter
// inputs render in their unfilled "default display" state on first paint.
// Picking a preset populates them via the preset-apply handler in the
// FeatureTreeSidebar. The empty variant is safe — the supabase edge function
// drops non-finite values, so the C# endpoint sees `Variant=null` and falls
// back to its hardcoded default (2 = Wheel_02), which is the LEAP71 reference
// configuration. Generate without picking still produces a sensible wheel.
export interface RoverWheelParams {
  variant: string;
  seed: string;
  outerRadius: string;
  hubRadius: string;
  width: string;
  treadPattern: string; // 'diamond' | 'chevron' | 'knob'
}
export const ROVER_WHEEL_DEFAULTS: RoverWheelParams = {
  variant: '',
  seed: '0',
  outerRadius: '',
  hubRadius: '',
  width: '',
  treadPattern: '',
};

// Helix Heat Exchanger — port of LEAP71_HelixHeatX. The upstream tutorial
// hardcodes every dimension (≈100×170×100mm envelope, 7mm port radius, 3.5mm
// plate thickness), so the user-tunable surface is empty for v1 — generation
// runs at the canonical reference values, built at 0.5mm and served as three
// fidelity tiers. Typed as `Record<string, never>` (not an empty interface) so
// the no-empty-object-type lint passes; the runtime contract is still an object,
// which is what implicit-chat's Object.entries(...) loop expects.
// The helix "feature tree" — every pipeline knob, tunable so their fidelity vs.
// build-time impact can be measured in one deploy. Strings to reuse the existing
// param-editing machinery; coerced server-side. Defaults are full-fidelity.
export interface HelixHeatXParams {
  // Geometry (tutorial values)
  plateThickness: string; // mm, default 3.5
  wallThickness: string; // mm, default 0.8
  ioRadius: string; // mm, default 7
  // Pipeline
  voxelSize: string; // mm PicoGK grid; smaller = finer/slower. default 0.5
  samplingStep: string; // mm lattice sampling; tutorial 0.005, larger = faster
  // 'glb' (indexed GLB: progressive shell→preview render + lossless GLB download,
  //   ~2.5–3× smaller than STL — the default) | 'full' (lossless STL download) |
  //   'tiers' (decimated Balanced/Overkill STL download tiers).
  transport: string;
  previewBudget: string; // triangle cap for the in-viewer preview mesh
}

// The exact LEAP71_HelixHeatX tutorial defaults (the real open-source part).
export const HELIX_HEAT_X_DEFAULTS: HelixHeatXParams = {
  plateThickness: '3.5',
  wallThickness: '0.8',
  ioRadius: '7',
  voxelSize: '0.5',
  samplingStep: '0.005',
  transport: 'glb',
  previewBudget: '300000',
};

// NASA Unitcell Engine params mirror the UnitcellDesign(name, L, W, H,
// thickness=...) signature from examples/examples.ipynb. Defaults match the
// "single point design" tutorial verbatim: unit aspect-ratio Octet with
// thickness 0.1 and the engine's default material (E=1, ν=0.3). String type
// to share the sidebar's existing number-as-string editing machinery.
export interface NasaLatticeParams {
  length: string;
  width: string;
  height: string;
  thickness: string;
}
export const NASA_LATTICE_DEFAULTS: NasaLatticeParams = {
  length: '1',
  width: '1',
  height: '1',
  thickness: '0.1',
};

// NASA Unitcell Lattice Converter — the params for filling an uploaded STL/STEP
// part with a lattice on the PicoGK voxel kernel (epic #309, Plan A). The
// lattice STYLE reuses the NasaLatticeTopology slugs (gyroid is the v1 default:
// self-supporting + already proven in the PicoGK prod path). `meshRef` is the
// uploaded part's object key in the private `meshes` bucket ('' until a part is
// uploaded, which gates Generate). All values are strings to share the
// sidebar's number-as-string editing machinery; the edge function coerces them.
//   density        — relative density 0–1 (how solid the lattice is)
//   quality        — 'draft' | 'balanced' | 'fine', maps to a voxel size server-side
//   skin           — keep a solid outer wall on the part surface (printability)
export interface LatticeFillParams {
  topology: NasaLatticeTopology;
  cellSize: string; // mm — unit cell size tiled through the part
  density: string; // relative density 0–1
  wallThickness: string; // mm — strut/wall thickness (advanced)
  skin: string; // 'true' | 'false' — solid outer skin
  skinThickness: string; // mm — thickness of that skin
  quality: string; // 'draft' | 'balanced' | 'fine'
  unit: string; // 'mm' | 'cm' | 'in' — units of the uploaded mesh
  meshRef: string; // object key in the `meshes` bucket ('' until uploaded)
  meshName: string; // original filename, for display
  previewUrl: string; // signed URL to a renderable STL of the ORIGINAL part
  // #315: B-rep edges of an uploaded STEP (base64 float32 segment pairs), so the
  // original-part preview draws SolidWorks-style edges incl. tangent. Empty for
  // STL uploads (no topology) — they fall back to the mesh-edge pass.
  edgesB64?: string;
  tangentEdgesB64?: string;
}
export const LATTICE_FILL_DEFAULTS: LatticeFillParams = {
  topology: 'nasa-lattice-gyroid',
  // cellSize / density / wallThickness are re-seeded per part once it's measured
  // (lattice-analyze) — these are just the pre-upload placeholders.
  cellSize: '8',
  density: '0.25',
  wallThickness: '0.6',
  skin: 'true',
  skinThickness: '1.0',
  quality: 'balanced',
  unit: 'mm',
  meshRef: '',
  meshName: '',
  previewUrl: '',
};

// Discriminated union covering every primitive — used when the active
// primitive's params get persisted into HomepageContext.
export type ImplicitPrimitiveParams =
  | { kind: 'base-box'; params: BaseBoxParams }
  | { kind: 'base-sphere'; params: BaseSphereParams }
  | { kind: 'base-cylinder'; params: BaseCylinderParams }
  | { kind: 'base-pipe'; params: BasePipeParams }
  | { kind: 'base-pipe-segment'; params: BasePipeSegmentParams }
  | { kind: 'base-ring'; params: BaseRingParams }
  | { kind: 'base-lens'; params: BaseLensParams }
  | { kind: 'super-ellipsoid'; params: SuperEllipsoidParams }
  | { kind: 'gyroid-sphere'; params: GyroidSphereParams }
  | { kind: 'gyroid-genus'; params: GyroidGenusParams }
  | { kind: 'lattice-pipe'; params: LatticePipeParams }
  | { kind: 'lattice-manifold'; params: LatticeManifoldParams }
  | { kind: 'rover-wheel'; params: RoverWheelParams }
  | { kind: 'helix-heat-x'; params: HelixHeatXParams }
  // Single discriminated variant for all 14 NASA lattice topologies — they
  // share the same (L, W, H, thickness) shape per the engine's
  // UnitcellDesign constructor, so we carry the topology as a sibling field
  // rather than minting 14 near-identical variants.
  | {
      kind: 'nasa-lattice';
      topology: NasaLatticeTopology;
      params: NasaLatticeParams;
    }
  // NASA Unitcell Lattice Converter — one converter variant; the lattice STYLE
  // lives in params.topology (not a sibling field) and the uploaded part is
  // referenced by params.meshRef.
  | {
      kind: 'lattice-fill';
      params: LatticeFillParams;
    };

// Response shape from POST /primitives/*. Matches ExecuteResponse in
// implicit-microservice/Program.cs — keep in sync.
export type ImplicitPrimitiveResponse = {
  stl_b64?: string;
  error?: string;
  // Tiered parts (helix): light preview inline + decimated download tiers.
  preview_stl_b64?: string;
  tiers?: {
    label: 'compressed' | 'balanced' | 'overkill';
    displayName: string;
    triangles: number;
    bytes: number;
    signedUrl?: string;
    stl_b64?: string;
    recommended?: boolean;
  }[];
};
